import { toBigInt } from "libs/bigint_helpers";
import type { AdditionalCoordinate } from "types/api_types";
import type { OrthoView, Point2, Vector3 } from "viewer/constants";
import { ContourModeEnum } from "viewer/constants";
import {
  getLayerByName,
  getVisibleSegmentationLayer,
} from "viewer/model/accessors/dataset_accessor";
import {
  getTransformsForLayer,
  globalToLayerTransformedPosition,
} from "viewer/model/accessors/dataset_layer_transformation_accessor";
import {
  calculateGlobalPos,
  type PositionWithRounding,
} from "viewer/model/accessors/view_mode_accessor";
import { updateUserSettingAction } from "viewer/model/actions/settings_actions";
import {
  addToContourListAction,
  finishEditingAction,
  floodFillAction,
  resetContourAction,
  setActiveCellAction,
  setContourTracingModeAction,
  startEditingAction,
  updateSegmentAction,
} from "viewer/model/actions/volumetracing_actions";
import {
  invertTransform,
  transformPointUnscaled,
} from "viewer/model/helpers/transformation_helpers";
import { api, Model, Store } from "viewer/singletons";
import type { WebknossosState } from "viewer/store";

export function handleDrawStart(pos: Point2, plane: OrthoView) {
  const state = Store.getState();
  const { layerPos, floatingLayerPos } = getLayerPositions(state, pos);

  Store.dispatch(setContourTracingModeAction(ContourModeEnum.DRAW));
  Store.dispatch(startEditingAction(layerPos, plane, floatingLayerPos));
  Store.dispatch(addToContourListAction(layerPos, floatingLayerPos));
}

function getLayerPositions(state: WebknossosState, pos: Point2) {
  const { rounded, floating } = calculateGlobalPos(state, pos);
  return {
    layerPos: getUntransformedSegmentationPosition(state, rounded),
    floatingLayerPos: getUntransformedSegmentationPosition(state, floating),
  };
}

function getUntransformedSegmentationPosition(state: WebknossosState, globalPos: Vector3) {
  /*
   * Converts the given position from world space to layer space.
   */
  const { nativelyRenderedLayerName } = state.datasetConfiguration;
  const maybeLayer = Model.getVisibleSegmentationLayer();
  if (maybeLayer == null) {
    throw new Error("Segmentation layer does not exist");
  }

  const layer = getLayerByName(state.dataset, maybeLayer.name);
  const segmentationTransforms = getTransformsForLayer(
    state.dataset,
    layer,
    nativelyRenderedLayerName,
  );
  const layerPos = transformPointUnscaled(invertTransform(segmentationTransforms))(globalPos);
  return layerPos;
}

export function handleEraseStart(pos: Point2, plane: OrthoView) {
  const state = Store.getState();
  const { layerPos, floatingLayerPos } = getLayerPositions(state, pos);

  Store.dispatch(setContourTracingModeAction(ContourModeEnum.DELETE));
  Store.dispatch(startEditingAction(layerPos, plane, floatingLayerPos));
  Store.dispatch(addToContourListAction(layerPos, floatingLayerPos));
}

export function handleMoveForDrawOrErase(pos: Point2) {
  const state = Store.getState();
  const { layerPos, floatingLayerPos } = getLayerPositions(state, pos);
  Store.dispatch(addToContourListAction(layerPos, floatingLayerPos));
}

export function handleEndForDrawOrErase() {
  Store.dispatch(finishEditingAction());
  Store.dispatch(resetContourAction());
}

export function handlePickCell(pos: Point2) {
  const state = Store.getState();
  const globalPos = calculateGlobalPos(state, pos);

  return handlePickCellFromGlobalPosition(globalPos, state.flycam.additionalCoordinates || []);
}

const _getSegmentIdForPosition = (mapped: boolean) => (globalPos: Vector3) => {
  // This function will return the currently loaded segment ID for a given position.
  // If the corresponding bucket is not loaded at the moment, the return value will be 0.
  // See getSegmentIdForPositionAsync if the bucket loading should be awaited before returning the ID.
  const layer = Model.getVisibleSegmentationLayer();
  const { additionalCoordinates } = Store.getState().flycam;

  if (!layer) {
    return 0n;
  }
  const posInLayerSpace = globalToLayerTransformedPosition(
    globalPos,
    layer.name,
    "segmentation",
    Store.getState(),
  );

  const segmentationCube = layer.cube;
  const segmentationLayerName = layer.name;
  const renderedZoomStepForCameraPosition = api.data.getRenderedZoomStepAtPosition(
    segmentationLayerName,
    posInLayerSpace,
  );

  return toBigInt(
    mapped
      ? segmentationCube.getMappedDataValue(
          posInLayerSpace,
          additionalCoordinates,
          renderedZoomStepForCameraPosition,
        )
      : segmentationCube.getDataValue(
          posInLayerSpace,
          additionalCoordinates,
          null,
          renderedZoomStepForCameraPosition,
        ),
  );
};

// This must not be memoized: the mapped segment id at a given position can change
// even when the position stays the same e.g. due to a foreign proofreading action.
export const getSegmentIdForPosition = _getSegmentIdForPosition(true);

// This must not be memoized on the position either: the looked-up value still depends on live
// state that is read inside the function (visible segmentation layer, additional coordinates,
// rendered zoom step) and can change while the queried position stays the same.
export const getUnmappedSegmentIdForPosition = _getSegmentIdForPosition(false);

const _getSegmentIdInfoForPosition = (globalPos: Vector3) => {
  // This function will return the currently loaded segment ID for a given position.
  // If the corresponding bucket is not loaded at the moment, the return value will be 0.
  // See getSegmentIdForPositionAsync if the bucket loading should be awaited before returning the ID.
  const layer = Model.getVisibleSegmentationLayer();
  const { additionalCoordinates } = Store.getState().flycam;

  if (!layer) {
    return { mapped: 0n, unmapped: 0n };
  }
  const posInLayerSpace = globalToLayerTransformedPosition(
    globalPos,
    layer.name,
    "segmentation",
    Store.getState(),
  );

  const segmentationCube = layer.cube;
  const segmentationLayerName = layer.name;
  const renderedZoomStepForCameraPosition = api.data.getRenderedZoomStepAtPosition(
    segmentationLayerName,
    posInLayerSpace,
  );

  return {
    mapped: toBigInt(
      segmentationCube.getMappedDataValue(
        posInLayerSpace,
        additionalCoordinates,
        renderedZoomStepForCameraPosition,
      ),
    ),
    unmapped: toBigInt(
      segmentationCube.getDataValue(
        posInLayerSpace,
        additionalCoordinates,
        null,
        renderedZoomStepForCameraPosition,
      ),
    ),
  };
};

// Not memoized for the same reasons as above: the returned mapped id can change due to foreign
// proofreading actions and the lookup depends on live state, so caching on the position can go stale.
export const getSegmentIdInfoForPosition = _getSegmentIdInfoForPosition;

export async function getSegmentIdForPositionAsync(globalPos: Vector3) {
  // This function will return the segment ID for a given position, awaiting the loading
  // of the corresponding bucket.
  // See getSegmentIdForPosition if the bucket loading should not be awaited.
  const layer = Model.getVisibleSegmentationLayer();
  const { additionalCoordinates } = Store.getState().flycam;

  if (!layer) {
    return 0n;
  }
  const posInLayerSpace = globalToLayerTransformedPosition(
    globalPos,
    layer.name,
    "segmentation",
    Store.getState(),
  );

  const segmentationCube = layer.cube;
  const segmentationLayerName = layer.name;
  const renderedZoomStepForCameraPosition = await api.data.getUltimatelyRenderedZoomStepAtPosition(
    segmentationLayerName,
    posInLayerSpace,
  );
  // Make sure the corresponding bucket is loaded
  await api.data.getDataValue(
    segmentationLayerName,
    posInLayerSpace,
    renderedZoomStepForCameraPosition,
    additionalCoordinates,
  );
  return toBigInt(
    segmentationCube.getMappedDataValue(
      posInLayerSpace,
      additionalCoordinates,
      renderedZoomStepForCameraPosition,
    ),
  );
}

function handlePickCellFromGlobalPosition(
  globalPos: PositionWithRounding,
  additionalCoordinates: AdditionalCoordinate[],
) {
  const segmentId = getSegmentIdForPosition(globalPos.rounded);

  if (segmentId < 0n) {
    return;
  }
  const visibleSegmentationLayer = getVisibleSegmentationLayer(Store.getState());
  if (visibleSegmentationLayer == null) {
    return;
  }
  const state = Store.getState();
  const layerPos = getUntransformedSegmentationPosition(state, globalPos.floating);
  Store.dispatch(setActiveCellAction(segmentId, layerPos, additionalCoordinates));

  Store.dispatch(
    updateSegmentAction(
      segmentId,
      {
        isVisible: true,
      },
      visibleSegmentationLayer.name,
      undefined,
      true,
    ),
  );
}

export function handleFloodFill(state: WebknossosState, screenPos: Point2, plane: OrthoView) {
  const globalPosRounded = calculateGlobalPos(Store.getState(), screenPos).rounded;
  handleFloodFillFromGlobalPosition(state, globalPosRounded, plane);
}

export function handleFloodFillFromGlobalPosition(
  state: WebknossosState,
  globalPos: Vector3,
  plane: OrthoView,
) {
  const positionInLayerSpace = getUntransformedSegmentationPosition(state, globalPos);
  Store.dispatch(floodFillAction(positionInLayerSpace, plane));
}

const MAX_BRUSH_CHANGE_VALUE = 5;
const BRUSH_CHANGING_CONSTANT = 0.02;

export function changeBrushSizeIfBrushIsActiveBy(factor: number) {
  const currentBrushSize = Store.getState().userConfiguration.brushSize;
  const newBrushSize =
    Math.min(Math.ceil(currentBrushSize * BRUSH_CHANGING_CONSTANT), MAX_BRUSH_CHANGE_VALUE) *
      factor +
    currentBrushSize;
  Store.dispatch(updateUserSettingAction("brushSize", newBrushSize));
}
